import { useRouteError } from 'react-router-dom'
import { Layout } from './layout'
import { Button } from './button'

/**
 * Detects chunk load failures caused by stale asset references after deploys.
 * When Firebase's SPA rewrite returns HTML for a missing JS chunk,
 * the browser throws a MIME type error. This catches it and reloads.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const msg = error.message || ''
  return (
    msg.includes('is not a valid JavaScript MIME type') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk')
  )
}

const RELOAD_KEY = 'chunk_error_reload'

export default function ChunkLoadErrorBoundary() {
  const error = useRouteError()

  if (isChunkLoadError(error)) {
    // Prevent infinite reload loops — only auto-reload once
    const lastReload = sessionStorage.getItem(RELOAD_KEY)
    const now = Date.now()

    if (!lastReload || now - Number(lastReload) > 10_000) {
      sessionStorage.setItem(RELOAD_KEY, String(now))
      window.location.reload()
      return null
    }
  }

  const errorMessage = error instanceof Error ? error.message : String(error)

  // Fallback UI for chunk errors that persist after reload,
  // or for any other unexpected errors
  return (
    <Layout title="Refresh Required">
      <div className="space-y-6">
        <p className="font-medium">
          A new version of Rundown Studio is available.
        </p>

        <div className="text-gray-500 space-y-2">
          <p>
            Please refresh the page to apply the latest update and continue
            where you left off.
          </p>
          <p>
            If this keeps happening, please contact us at{' '}
            <a
              href="mailto:hello@rundownstudio.app"
              className="underline hover:text-gray-400"
            >
              hello@rundownstudio.app
            </a>
          </p>
        </div>

        <Button
          text="Refresh Now"
          onClick={() => {
            sessionStorage.removeItem(RELOAD_KEY)
            window.location.reload()
          }}
        />

        <hr className="border-gray-800" />

        {/* We include the actual error message here for when people send us screenshots */}
        <div className="text-sm text-gray-700 text-center">
          &quot;{errorMessage}&quot;
        </div>
      </div>
    </Layout>
  )
}
